import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EditIcon from "@mui/icons-material/Edit";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { IconButton } from "@mui/material";
import { ReactElement, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import WhiteButton from "../../components/button/white-button";
import EditAssetDialog from "../../components/dialogs/asset/edit-asset";
import UserRole from "../../components/user-role/user-role";
import {
  Asset,
  AssetFilter,
  UpdateAssetInput,
} from "../../generated/gql/graphql";
import useAssets from "../../hooks/query/asset/use-assets";
import { parseType } from "../../utils/graphql";
import { cn } from "../../utils/utils";

import AssetVersionSelector from "./asset-version-selector";

type Props = {
  asset: Asset;
  isEdit: boolean;

  setIsEdit(value: boolean): void;
  setUpdate(value: UpdateAssetInput | undefined): void;
  onUpdate(): void;
};

function searchParamsToAssetFilter(searchParams: URLSearchParams): AssetFilter {
  const out: AssetFilter = {};

  if (searchParams.has("status")) {
    out.status = {
      eq: searchParams.get("status"),
    };
  }

  if (searchParams.has("assetTypes")) {
    out.assetTypes = {
      eq: searchParams.get("assetTypes"),
    };
  }

  return out;
}

export default function Header(props: Props): ReactElement {
  const { asset, isEdit, setIsEdit, setUpdate, onUpdate } = props;

  const [searchParams] = useSearchParams();
  const pageValue = parseInt(searchParams.get("page") || "1") || 1;

  const [renameModalState, setRenameModalState] = useState(false);
  const [nextAssetId, setNextAssetId] = useState<string>();
  const [previousAssetId, setPreviousAssetId] = useState<string>();

  // Note: Currently it works only for the previous active page.
  const { data } = useAssets({
    filter: searchParamsToAssetFilter(searchParams),
    pagination: {
      page: pageValue,
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const assets = parseType<Asset[]>("assets", data);
    if (!assets) {
      return;
    }

    const idx = assets.findIndex((v) => v.id === asset.id);
    if (idx === -1) {
      return;
    }

    if (idx + 1 != assets.length && assets[idx + 1]) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setNextAssetId(assets[idx + 1].id);
    }

    if (idx - 1 != -1 && assets[idx - 1]) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setPreviousAssetId(assets[idx - 1].id);
    }
  }, [data]);

  function handleChangeToggleModal(): void {
    setRenameModalState(!renameModalState);
  }

  function onPreviousAsset(): void {
    window.location.href = `/file/${previousAssetId}/${1}${
      window.location.search
    }`;
  }

  function onNextAsset(): void {
    window.location.href = `/file/${nextAssetId}/${1}${window.location.search}`;
  }

  return (
    <>
      <div className="flex w-full justify-between">
        <div className="flex flex-col w-full min-w-lg space-y-10">
          <div className="flex space-x-1 items-center">
            <Link className="cursor-pointer" to={`/`}>
              <ArrowBackIosIcon className="h-[16px] w-[16px]" />
              Back to Assets
            </Link>
          </div>
          <div className="flex flex-row flex-wrap w-full h-fit">
            <div className="w-[50%] text-right flex">
              <h2
                style={{ color: "#DD2127" }}
                className="text-[36px] whitespace-nowrap overflow-x-hidden text-ellipsis leading-tight"
              >
                {asset?.name}
              </h2>
              <UserRole>
                <IconButton
                  onClick={handleChangeToggleModal}
                  className="ml-10 w-[45px]"
                  size="small"
                >
                  <ModeEditOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </UserRole>
            </div>
          </div>
          <div className="h-fit">
            {asset && asset.parentId && <AssetVersionSelector asset={asset} />}
          </div>
        </div>
        <div className="flex gap-10 items-start mr-15">
          <WhiteButton
            text={isEdit ? "save changes" : "edit mode"}
            onClick={() => {
              onUpdate();
              setIsEdit(!isEdit);
            }}
            icon={isEdit ? <SaveOutlinedIcon /> : <EditIcon />}
          />
          <WhiteButton
            hidden={!isEdit}
            onClick={() => {
              setIsEdit(!isEdit);
              setUpdate(undefined);
            }}
            text="Cancel"
          />
        </div>
        <div className="flex items-start mt-15">
          <ArrowBackIosIcon
            className={cn(
              "cursor-pointer shrink-0",
              !previousAssetId ? "hidden" : ""
            )}
            onClick={() => onPreviousAsset()}
          />
          <p className="shrink-0 text-[16px] px-5 uppercase">Next Assets</p>
          <ArrowForwardIosIcon
            className={cn(
              "cursor-pointer shrink-0",
              !nextAssetId ? "hidden" : ""
            )}
            onClick={() => onNextAsset()}
          />
        </div>
      </div>
      <EditAssetDialog
        isOpen={renameModalState}
        asset={asset}
        onClose={handleChangeToggleModal}
      />
    </>
  );
}
